/**
 * Image Generation Store
 * Manages image generation state with Zustand
 * Validates: Requirements 16.1, 16.2, 16.5
 */
import { create } from 'zustand';
import apiClient from '../api/client';

/**
 * Image generation settings
 */
export interface ImageGenerationSettings {
  size?: '256x256' | '512x512' | '1024x1024' | '1024x1792' | '1792x1024';
  quality?: 'standard' | 'hd';
  style?: 'vivid' | 'natural';
  n?: number;
}

/**
 * Image result from generation
 */
export interface ImageResult {
  url: string;
  revisedPrompt?: string;
}

/**
 * Image generation job
 */
export interface ImageJob {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  provider: string;
  model: string;
  prompt: string;
  enhancedPrompt?: string;
  settings: ImageGenerationSettings;
  images?: ImageResult[];
  error?: string;
  createdAt: Date;
  completedAt?: Date | null;
}

/**
 * Image model info
 */
export interface ImageModelInfo {
  id: string;
  name: string;
}

/**
 * Image provider info
 */
export interface ImageProviderInfo {
  id: string;
  name: string;
  models: ImageModelInfo[];
}

interface ImageState {
  // Jobs
  jobs: ImageJob[];
  currentJob: ImageJob | null;
  jobsLoading: boolean;
  
  // Providers and models
  providers: ImageProviderInfo[];
  providersLoading: boolean;
  selectedProvider: string;
  selectedModel: string;
  
  // Generation state
  isGenerating: boolean;
  error: string | null;
  
  // Settings
  promptEnhancerEnabled: boolean;
  settings: ImageGenerationSettings;
  
  // Actions
  loadProviders: () => Promise<void>;
  loadJobs: () => Promise<void>;
  loadJob: (jobId: string) => Promise<void>;
  generateImage: (prompt: string) => Promise<void>;
  setSelectedProvider: (provider: string) => void;
  setSelectedModel: (model: string) => void;
  setSettings: (settings: Partial<ImageGenerationSettings>) => void;
  togglePromptEnhancer: () => void;
  clearError: () => void;
  pollJobStatus: (jobId: string) => Promise<void>;
}

export const useImageStore = create<ImageState>((set, get) => ({
  // Initial state
  jobs: [],
  currentJob: null,
  jobsLoading: false,
  providers: [],
  providersLoading: false,
  selectedProvider: 'openai',
  selectedModel: 'dall-e-3',
  isGenerating: false,
  error: null,
  promptEnhancerEnabled: false,
  settings: {
    size: '1024x1024',
    quality: 'standard',
    style: 'vivid',
    n: 1,
  },

  /**
   * Load available providers and models
   */
  loadProviders: async () => {
    set({ providersLoading: true });
    try {
      const response = await apiClient.get('/api/images/providers');
      const providers = response.data.providers as ImageProviderInfo[];
      set({ providers, providersLoading: false });
      
      // Set default selection if not already set
      if (providers.length > 0 && providers[0]!.models.length > 0) {
        const state = get();
        if (!state.selectedProvider || !state.selectedModel) {
          set({
            selectedProvider: providers[0]!.id,
            selectedModel: providers[0]!.models[0]!.id,
          });
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load providers';
      set({ error: message, providersLoading: false });
    }
  },

  /**
   * Load image generation jobs
   * Validates: Requirements 16.5
   */
  loadJobs: async () => {
    set({ jobsLoading: true });
    try {
      const response = await apiClient.get('/api/images');
      const jobs = (response.data.jobs as ImageJob[]).map(job => ({
        ...job,
        createdAt: new Date(job.createdAt),
        completedAt: job.completedAt ? new Date(job.completedAt) : null,
      }));
      set({ jobs, jobsLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load jobs';
      set({ error: message, jobsLoading: false });
    }
  },

  /**
   * Load a specific job
   */
  loadJob: async (jobId: string) => {
    try {
      const response = await apiClient.get(`/api/images/${jobId}`);
      const job = {
        ...response.data.job,
        createdAt: new Date(response.data.job.createdAt),
        completedAt: response.data.job.completedAt 
          ? new Date(response.data.job.completedAt) 
          : null,
      } as ImageJob;
      set({ currentJob: job });
      
      // Update in jobs list
      const jobs = get().jobs.map(j => j.id === jobId ? job : j);
      set({ jobs });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load job';
      set({ error: message });
    }
  },

  /**
   * Generate a new image
   * Validates: Requirements 16.2, 16.3
   */
  generateImage: async (prompt: string) => {
    const state = get();
    set({ isGenerating: true, error: null });

    try {
      // Enhance prompt if enabled
      let enhancedPrompt: string | undefined;
      if (state.promptEnhancerEnabled) {
        try {
          const enhanceResponse = await apiClient.post('/api/enhance', {
            prompt,
            targetType: 'image',
            model: state.selectedModel,
          });
          enhancedPrompt = enhanceResponse.data.enhancedPrompt;
        } catch {
          // Continue without enhancement if it fails
          console.warn('Prompt enhancement failed, using original prompt');
        }
      }

      // Create image generation job
      const response = await apiClient.post('/api/images/generate', {
        prompt,
        provider: state.selectedProvider,
        model: state.selectedModel,
        settings: state.settings,
        enhancedPrompt,
      });

      const job = {
        ...response.data.job,
        createdAt: new Date(response.data.job.createdAt),
        completedAt: response.data.job.completedAt 
          ? new Date(response.data.job.completedAt) 
          : null,
      } as ImageJob;

      // Add to jobs list
      set({ 
        jobs: [job, ...state.jobs],
        currentJob: job,
        isGenerating: false,
      });

      // Start polling for status
      get().pollJobStatus(job.id);
    } catch (error: unknown) {
      let message = 'Failed to generate image';
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { message?: string } } };
        message = axiosError.response?.data?.message || message;
      } else if (error instanceof Error) {
        message = error.message;
      }
      set({ error: message, isGenerating: false });
    }
  },

  /**
   * Poll job status until completion
   */
  pollJobStatus: async (jobId: string) => {
    const pollInterval = 2000; // 2 seconds
    const maxAttempts = 60; // ~2 minutes

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise(resolve => setTimeout(resolve, pollInterval));

      try {
        const response = await apiClient.get(`/api/images/${jobId}`);
        const job = {
          ...response.data.job,
          createdAt: new Date(response.data.job.createdAt),
          completedAt: response.data.job.completedAt 
            ? new Date(response.data.job.completedAt) 
            : null,
        } as ImageJob;

        // Update current job if it matches
        const state = get();
        if (state.currentJob?.id === jobId) {
          set({ currentJob: job });
        }

        // Update in jobs list
        const jobs = state.jobs.map(j => j.id === jobId ? job : j);
        set({ jobs });

        // Stop polling if job is complete or failed
        if (job.status === 'completed' || job.status === 'failed') {
          break;
        }
      } catch {
        // Continue polling on error
      }
    }
  },

  setSelectedProvider: (provider: string) => {
    const state = get();
    const providerInfo = state.providers.find(p => p.id === provider);
    const firstModel = providerInfo?.models[0]?.id || '';
    set({ selectedProvider: provider, selectedModel: firstModel });
  },

  setSelectedModel: (model: string) => {
    set({ selectedModel: model });
  },

  setSettings: (settings: Partial<ImageGenerationSettings>) => {
    set({ settings: { ...get().settings, ...settings } });
  },

  togglePromptEnhancer: () => {
    set({ promptEnhancerEnabled: !get().promptEnhancerEnabled });
  },

  clearError: () => {
    set({ error: null });
  },
}));
